import React, { useEffect, useState } from "react";
import useStyles from "../../../../../components/common/model/styles";
import Popup from "../../../../../components/common/model/Popup";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { CheckCircleOutline } from "@mui/icons-material";
import closeIcon from "../../../../../assets/closeModel.png";
import CustomButton from "../../../../../components/common/button/CustomButton";
import { useDispatch, useSelector } from "react-redux";
import {
  getClientInventoryCrewListAction,
  sendInventoryCrewSendLinkAction,
  setSendInventoryCrewSendLinkAction,
} from "../../../../../store/reducers/inventoryCrewSlice";

interface AlertAssignCrewProps {
  open: boolean;
  sourceRemovalId?: string | number | null;
  contractId?: string | number | null;
  title?: string;
  onHide?: () => void;
  content?: any;
  isError?: any;
  setIsValid?: any;
  setAlertModelShow?: any;
  // submitHandler?: () => void;
}

const AlertAssignCrew: React.FC<AlertAssignCrewProps> = ({
  open,
  sourceRemovalId,
  contractId,
  title,
  onHide = () => {},
  content,
  setIsValid,
  isError,
  setAlertModelShow,
  // submitHandler = () => {}
}) => {
  const classes = useStyles();
  const { clientInventoryCrewList, isSendInventoryCrewSendLinkResponse } =
    useSelector((state: any) => state.inventoryCrew || {});
  const dispatch = useDispatch();
  const hasSourceRemoval = sourceRemovalId !== null && sourceRemovalId !== undefined && sourceRemovalId !== "";
  const [step, setStep] = useState(0);
  const [selectedCrewId, setSelectedCrewId] = useState("");
  const [email, setEmail] = useState("");
  const [manualEmail, setManualEmail] = useState(false);
  const selectedCrew = clientInventoryCrewList?.find(
    (crew: any) => String(crew?.id ?? crew?._id) === selectedCrewId,
  );
  const crewEmail =
    selectedCrew?.email ?? selectedCrew?.email_address ?? selectedCrew?.emailAddress ?? "";
  const hasValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const canAssign = manualEmail
    ? hasValidEmail
    : Boolean(selectedCrew) && hasValidEmail;

  useEffect(() => {
    if (open) {
      if(hasSourceRemoval){
        dispatch(getClientInventoryCrewListAction({removalLeadId:sourceRemovalId}));
      }
      setStep(hasSourceRemoval ? 0 : 2);
      setSelectedCrewId("");
      setEmail("");
      setManualEmail(!hasSourceRemoval);
      dispatch(setSendInventoryCrewSendLinkAction(false));
    }
  }, [dispatch, hasSourceRemoval, open]);

  useEffect(() => {
    if (isSendInventoryCrewSendLinkResponse) {
      setStep(3);
    }
  }, [isSendInventoryCrewSendLinkResponse]);

  const handleCrewChange = (crewId: string) => {
    setSelectedCrewId(crewId);
    const crew = clientInventoryCrewList?.find(
      (item: any) => String(item?.id ?? item?._id) === crewId,
    );
    setEmail(
      crew?.email ?? crew?.email_address ?? crew?.emailAddress ?? "",
    );
  };

  const closeModel = () => {
    onHide();
    setAlertModelShow?.(false);
  };

  const handleCrewPath = () => {
    setManualEmail(false);
    setStep(1);
  };

  const handleManualEmailPath = () => {
    setManualEmail(true);
    setSelectedCrewId("");
    setEmail("");
    setStep(2);
  };

  const handleAssign = () => {
    if (!canAssign || contractId === null || contractId === undefined || contractId === "") return;
    dispatch(setSendInventoryCrewSendLinkAction(false));
    dispatch(
      sendInventoryCrewSendLinkAction({
        crewId: manualEmail ? null : selectedCrewId || null,
        email: email.trim(),
        contractId,
      }),
    );
  };

  const handleBack = () => {
    if (step === 3) {
      setStep(manualEmail ? 2 : 1);
      return;
    }
    if (!hasSourceRemoval) {
      closeModel();
      return;
    }
    setStep(0);
  };

  return (
    <Popup
      open={open}
      title={title || ""}
      onHide={onHide}
      fullWidth={true}
      maxWidth="sm"
      aria-describedby="alert-dialog-slide-description"
      header={
        <>
          <img
            onClick={closeModel}
            src={closeIcon}
            className={classes.closeicons}
            alt="close"
          />
        </>
      }
      footer={
        <>
          {isError ? (
            <CustomButton variant="outlined" title="Close" onClick={closeModel} />
          ) : step === 0 ? (
            <>
              <CustomButton variant="outlined" title="No" onClick={handleManualEmailPath} />
              <CustomButton title="Yes" onClick={handleCrewPath} />
            </>
          ) : step === 1 || step === 2 ? (
            <>
              <CustomButton
                variant="outlined"
                title="Back"
                onClick={handleBack}
              />
              <CustomButton
                title="Assign"
                onClick={handleAssign}
                disabled={!canAssign || contractId === null || contractId === undefined || contractId === ""}
              />
            </>
          ) : (
            <>
              <CustomButton variant="outlined" title="Back" onClick={handleBack} />
              <CustomButton variant="outlined" title="Done" onClick={closeModel} />
            </>
          )}
        </>
      }
    >
      <Box sx={{ px: 3, pt: 1, pb: 2 }}>
        {step === 0 ? (
          <Box sx={{ display: "grid", gap: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#24312d" }}>
                Send to the assigned crew?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Choose Yes to select a crew member, or No to enter an email address manually.
              </Typography>
            </Box>
          </Box>
        ) : step === 1 || step === 2 ? (
          <Box sx={{ display: "grid", gap: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#24312d" }}>
                {manualEmail ? "Enter crew email" : "Select assigned crew"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {manualEmail
                  ? "Enter the email address that should receive inventory access."
                  : "Choose a worker assigned to this job. Their email will appear below when available."}
              </Typography>
            </Box>
            {!manualEmail && (
            <FormControl fullWidth size="small">
              <InputLabel id="inventory-crew-label">Crew member</InputLabel>
              <Select
                labelId="inventory-crew-label"
                value={selectedCrewId}
                label="Crew member"
                onChange={(event) => handleCrewChange(event.target.value)}
              >
                {clientInventoryCrewList?.map((crew: any) => {
                  const crewId = String(crew?.id ?? crew?._id ?? "");
                  return (
                    <MenuItem key={crewId} value={crewId}>
                      {crew?.name ?? crew?.name ?? "Crew member"}
                      {/* {crew?.role ? ` - ${crew.role}` : ""} */}
                    </MenuItem>
                  );
                })}
                {clientInventoryCrewList?.length === 0 && (
                  <MenuItem value="" disabled>
                    No crew members available
                  </MenuItem>
                )}
              </Select>
            </FormControl>
            )}
            {(manualEmail || selectedCrewId) && (
            <TextField
              fullWidth
              size="small"
              label="Crew email"
              placeholder="Enter email address"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              helperText={
                !manualEmail && crewEmail
                  ? "Email from the crew profile. You can edit it."
                  : "Enter the email address to use for inventory access."
              }
            />
            )}
          </Box>
        ) : (
          <Box sx={{ display: "grid", gap: 2 }}>
            <Box>
              <CheckCircleOutline sx={{ color: "#16825d" }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#24312d" }}>
                Crew assigned
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {manualEmail
                  ? email
                  : `${selectedCrew?.name ?? selectedCrew?.full_name ?? "Crew member"} - ${email}`}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    </Popup>
  );
};

export default AlertAssignCrew;
